import { useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Center,
  Drawer,
  Group,
  Loader,
  Progress,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCircleCheck, IconCircleDashed, IconPlus } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { ProjectLifecycleStage } from '@ledokol/contracts'
import { appendLifecycleEvent, fetchLifecycleStatus } from '../../shared/api/project-crm'
import { LifecycleChecklist } from './lifecycle-checklist'

const STAGES: { value: ProjectLifecycleStage; label: string; description: string }[] = [
  { value: 'lead', label: 'Лид', description: 'Первичный контакт' },
  { value: 'proposal', label: 'МП', description: 'Медиа-план готов' },
  { value: 'approval', label: 'Согласование', description: 'Клиент рассматривает' },
  { value: 'contract', label: 'Договор', description: 'Документы подписаны' },
  { value: 'active_flight', label: 'Флайт', description: 'Кампания идёт' },
  { value: 'post_flight', label: 'Пост-флайт', description: 'Завершена, оцениваем' },
  { value: 'repeat_request', label: 'Повторно', description: 'Клиент вернулся' },
  { value: 'lost', label: 'Потерян', description: 'Отказались / не продолжили' },
]

const STAGE_LABEL: Record<ProjectLifecycleStage, string> = Object.fromEntries(
  STAGES.map((s) => [s.value, s.label]),
) as Record<ProjectLifecycleStage, string>

const STAGE_COLOR: Record<ProjectLifecycleStage, string> = {
  lead: 'gray',
  proposal: 'blue',
  approval: 'cyan',
  contract: 'indigo',
  active_flight: 'orange',
  post_flight: 'grape',
  repeat_request: 'teal',
  lost: 'red',
}

export function LifecycleTab({ projectId }: { projectId: string }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['lifecycle', projectId],
    queryFn: () => fetchLifecycleStatus(projectId),
  })

  const [addOpen, setAddOpen] = useState(false)
  const [newStage, setNewStage] = useState<ProjectLifecycleStage | null>(null)
  const [note, setNote] = useState('')

  const appendMut = useMutation({
    mutationFn: () =>
      appendLifecycleEvent(projectId, { stage: newStage!, note: note || null }),
    onSuccess: () => {
      notifications.show({ message: 'Стадия обновлена', color: 'green' })
      setAddOpen(false)
      setNewStage(null)
      setNote('')
      qc.invalidateQueries({ queryKey: ['lifecycle', projectId] })
    },
    onError: (e: unknown) => {
      const msg = (e instanceof Error && e.message) || 'Не удалось'
      notifications.show({ message: msg, color: 'red' })
    },
  })

  if (isLoading) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    )
  }

  // Прогресс-бар: на основной воронке (без lost) — какая стадия достигнута.
  const mainStages: ProjectLifecycleStage[] = ['lead', 'proposal', 'approval', 'contract', 'active_flight', 'post_flight']
  const currentIdx = data?.current_stage ? mainStages.indexOf(data.current_stage) : -1
  const progress = currentIdx >= 0 ? ((currentIdx + 1) / mainStages.length) * 100 : 0

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group>
          <Text size="sm" c="dimmed">Текущая стадия:</Text>
          {data?.current_stage ? (
            <Badge size="lg" color={STAGE_COLOR[data.current_stage]} variant="filled">
              {STAGE_LABEL[data.current_stage]}
            </Badge>
          ) : (
            <Badge size="lg" color="gray" variant="light">Не задана</Badge>
          )}
          {data?.current_since && (
            <Text size="sm" c="dimmed">
              с {dayjs(data.current_since).format('D MMM YYYY')}
            </Text>
          )}
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setAddOpen(true)}>
          Перевести на стадию
        </Button>
      </Group>

      {/* Прогресс по основной воронке */}
      <Box>
        <Progress
          value={progress}
          color={data?.current_stage === 'lost' ? 'red' : 'blue'}
          size="lg"
          radius="xl"
        />
        <Group justify="space-between" mt="xs" wrap="nowrap" style={{ overflowX: 'auto' }}>
          {mainStages.map((s, i) => (
            <Stack key={s} gap={2} align="center" miw={80}>
              {i <= currentIdx ? (
                <IconCircleCheck size={20} color="#2563eb" />
              ) : (
                <IconCircleDashed size={20} color="#94a3b8" />
              )}
              <Text size="xs" ta="center" c={i <= currentIdx ? undefined : 'dimmed'}>
                {STAGE_LABEL[s]}
              </Text>
            </Stack>
          ))}
        </Group>
      </Box>

      {/* Журнал переходов */}
      <Box>
        <Text size="sm" fw={600} mb="xs">История переходов</Text>
        {data?.history.length === 0 ? (
          <Text size="sm" c="dimmed">Стадии ещё не отмечены. Нажмите «Перевести на стадию», чтобы начать.</Text>
        ) : (
          <Stack gap="xs">
            {data?.history.map((e) => (
              <Group key={e.id} gap="md" wrap="nowrap">
                <Badge color={STAGE_COLOR[e.stage]} variant="light" miw={140}>
                  {STAGE_LABEL[e.stage]}
                </Badge>
                <Text size="sm" c="dimmed" miw={160}>
                  {dayjs(e.entered_at).format('D MMM YYYY, HH:mm')}
                </Text>
                <Text size="sm" c="dimmed">
                  {e.actor?.full_name ?? '—'}
                </Text>
                {e.note && <Text size="sm">{e.note}</Text>}
              </Group>
            ))}
          </Stack>
        )}
      </Box>

      {/* #10 — чеклисты по стадиям */}
      <LifecycleChecklist projectId={projectId} />

      <Drawer
        opened={addOpen}
        onClose={() => setAddOpen(false)}
        title="Перевести проект на новую стадию"
        position="right"
        size="md"
      >
        <Stack>
          <Select
            label="Стадия"
            data={STAGES.map((s) => ({ value: s.value, label: `${s.label} — ${s.description}` }))}
            value={newStage}
            onChange={(v) => setNewStage(v as ProjectLifecycleStage | null)}
            required
          />
          <Textarea
            label="Заметка (опц.)"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            autosize
            minRows={3}
            placeholder="Например: «Проведена встреча с CMO, отправили коммерческое»"
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAddOpen(false)}>Отмена</Button>
            <Button
              loading={appendMut.isPending}
              disabled={!newStage}
              onClick={() => appendMut.mutate()}
            >
              Сохранить
            </Button>
          </Group>
        </Stack>
      </Drawer>
    </Stack>
  )
}
